import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import Layout from '../UI/introGameLayout/layout';

import {
  gamesQuery,
  getGamesSessions,
  studentGeneralInfoQuery,
  scenarioLevelsQuery,
  scenarioLevelsViewQuery,
  scenarioLevelsItemsViewQuery,
  createGameSession,
  coursesQuery
} from './api';

import useAuth from '../../auth/useAuth';

import { getHighestActiveGame } from '../../utils';

import TransitionDefault from '../UI/transition/default';
import TransitionInitial from '../UI/transition/initial';
import Alert from '../UI/alert';

export default function Transition() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    getUser,
    isInstructor,
    isStudent,
    getCurrentClassificationGameId,
    getCurrentNormalGameId,
    getCurrentBalancedEquationGameId,
    getCurrentThePrinciplesGameId,
    getTransitionGameType
  } = useAuth();
  const user = getUser();

  const [transitionType, setTransitionType] = useState(null);
  const [levelType, setLevelType] = useState(null);

  const [redirectUrl, setRedirectUrl] = useState('/assignments');
  const [isDataReady, setIsDataReady] = useState(false);

  const mountTimeRef = useRef(Date.now());

  const gameType = getTransitionGameType();
  const [currentGameId, setCurrentGameId] = useState(0);
  const [scenarioId, setScenarioId] = useState(0);

  const isClassification = gameType?.includes('Account Classification');
  const isNormal = gameType?.includes('Normal Balance');
  const isEquation = gameType?.includes('Balanced Equation');
  const isPrinciples = gameType?.includes('The Principles');

  const { data: studentGeneralInfo, dataUpdatedAt: dataUpdatedAtStudentGeneralInfo } = useQuery({
    ...studentGeneralInfoQuery(),
    refetchOnMount: 'always',
    enabled: isStudent(),
  });

  const { data: instructorGames } = useQuery({
    ...gamesQuery(),
    enabled: isInstructor(),
  });

  const { data: instructorGamesSessions, dataUpdatedAt: dataUpdatedAtInstructorGamesSessions } = useQuery({
    queryKey: ['gamesSessionsTransition'],
    queryFn: getGamesSessions,
    refetchOnMount: 'always',
    enabled: isInstructor(),
  });

  const games = isStudent() ? studentGeneralInfo?.games : instructorGames;
  const gamesSessions = isStudent() ? studentGeneralInfo?.sessions : instructorGamesSessions;
  const dataUpdatedAtGamesSessions = isStudent() ? dataUpdatedAtStudentGeneralInfo : dataUpdatedAtInstructorGamesSessions;

  const { data: scenarioLevels } = useQuery(scenarioLevelsQuery(scenarioId));

  useQuery(scenarioLevelsViewQuery((isEquation || isPrinciples) ? scenarioId : 0));
  useQuery(scenarioLevelsItemsViewQuery((isClassification || isNormal) ? scenarioId : 0));

  const { data: courses } = useQuery(coursesQuery({transitionType}));

  const [sessionError, setSessionError] = useState(null);

  const createGameSessionMutation = useMutation({
    mutationFn: (data) => createGameSession(data),
    onSuccess: () => {
      // Force any consumers of generalInfo (level pages, etc.) to refetch
      // so the freshly-created session is visible. Without this, queries
      // marked staleTime: Infinity will keep serving the pre-session cache
      // and the level page bounces the user back to /tutorial.
      queryClient.invalidateQueries({ queryKey: ['generalInfo'] });
    },
    onError: (err) => {
      // 400 { game: ["You are not a member of this game's course."] } for a student without access; everything else lands under the generic message.
      const notAMember = err?.response?.status === 400 && /not a member/i.test(JSON.stringify(err?.response?.data || ''));
      setSessionError({
        type: 'error',
        title: notAMember ? 'No Access' : 'Something Went Wrong',
        text: notAMember
          ? 'You don\'t have access to this exercise. Please contact your instructor.'
          : 'We couldn\'t start the exercise. Please return to your assignments and try again.'
      });
    }
  });

  // Sync guard: mutation.isPending updates asynchronously, so a re-firing effect can pass the check before React sees the pending state and stampede the endpoint. A ref set synchronously closes that window.
  const isCreatingSessionRef = useRef(false);
  const safeCreateSession = (data) => {
    if (isCreatingSessionRef.current) return;
    isCreatingSessionRef.current = true;
    createGameSessionMutation.mutateAsync(data).finally(() => {
      isCreatingSessionRef.current = false;
    });
  };

  useEffect(() => {
    // Reset state
    setTransitionType(null);
    setIsDataReady(false);

    // Invalidate all relevant queries to force fresh fetch
    queryClient.invalidateQueries({ queryKey: ['gamesSessionsTransition'] });
  }, [queryClient]);

  useEffect(() => {
    if (games && gameType) {
      const selectedGameId = isClassification
        ? getCurrentClassificationGameId()
        : isNormal
          ? getCurrentNormalGameId()
          : isEquation
            ? getCurrentBalancedEquationGameId()
            : isPrinciples
              ? getCurrentThePrinciplesGameId()
              : 0;

      const game = selectedGameId
        ? games.find(game => game.id === selectedGameId)
        : getHighestActiveGame(games);

      if (game && game.scenario) {
        setCurrentGameId(game.id);
        setScenarioId(game.scenario);
      }
    }
  }, [games, gameType, isClassification, isNormal, isEquation, isPrinciples]);

  // Detect tournament game so we can skip this transition screen entirely
  const currentGame = games?.find(game => game.id === currentGameId);
  const isTournamentGame = currentGame?.tournament_ids?.length > 0;

  useEffect(() => {
    if (!isDataReady || !redirectUrl) return;

    // For tournament games, navigate immediately — no intro screen, no
    // delay. Also strip trailing /tutorial, /intro, or /level-intro
    // segments so instructors previewing in front of a class land
    // directly on the actual level page instead of a walkthrough that
    // eats classroom time. Trailing-only matches so paths that just
    // happen to contain the word (e.g. a level slug) aren't affected.
    if (isTournamentGame) {
      const directUrl = redirectUrl
        .replace(/\/tutorial$/, '')
        .replace(/\/level-intro$/, '')
        .replace(/\/intro$/, '');
      navigate(directUrl, { replace: true });
      return;
    }

    if (transitionType !== 'default') return;

    const MINIMUM_DISPLAY_TIME = 3000;
    const elapsed = Date.now() - mountTimeRef.current;
    const remainingTime = Math.max(0, MINIMUM_DISPLAY_TIME - elapsed);

    const timer = setTimeout(() => {
      navigate(redirectUrl, { replace: true });
    }, remainingTime);

    return () => clearTimeout(timer);
  }, [transitionType, isDataReady, redirectUrl, navigate, isTournamentGame]);

  useEffect(() => {
    if (!scenarioLevels?.length
      || !games?.length
      || !gamesSessions
      || dataUpdatedAtGamesSessions < mountTimeRef.current
    ) {
      return;
    }

    let redirectUrl = '/assignments';

    const sortedScenarioLevels = scenarioLevels.filter(scenarioLevel => scenarioLevel.level !== null).sort((a, b) => a.level - b.level);

    // Realco levels share a single "type" for the intro screen — any
    // `real-public-co-*` level (Apple, Costco, …) should only show the
    // realco transition once across the whole game type. Match by the
    // `real-public-co` prefix so completing one realco company counts
    // toward the others.
    const REALCO_PREFIX = 'real-public-co';
    const matchesLevelName = (storedName, targetName) => {
      if (storedName == null || targetName == null) return false;
      if (targetName.startsWith(REALCO_PREFIX)) {
        return storedName.startsWith(REALCO_PREFIX);
      }
      return storedName === targetName;
    };

    const hasCompletedLevelTypeBefore = (levelName, sessionsForGameType) => {
      return sessionsForGameType.some(session => {
        const levelNames = session.level_names || {};
        return Object.entries(levelNames).some(([levelKey, name]) => {
          return matchesLevelName(name, levelName) && session[levelKey] === 'completed';
        });
      });
    };

    const hasAnyDecisionsForLevelType = (levelName, sessionsForGameType) => {
      return sessionsForGameType.some(session => {
        const levelNames = session.level_names || {};
        return Object.entries(levelNames).some(([levelKey, name]) => {
          return matchesLevelName(name, levelName) && session[levelKey] !== null && session[levelKey] !== 'not_started';
        });
      });
    };

    const processTransition = async () => {
      const currentGameSessions = gamesSessions?.filter(
        session => session.game === currentGameId && session.user === user?.id
      ) || [];

      const sessionsForGameType = gamesSessions.filter(
        session =>
          session.user === user?.id &&
          games.find(game => game.id === session.game && game.game_type === gameType)
      );

      const hasAnySession = sessionsForGameType.length > 0;

      if (isClassification) {
        let determinedLevelType = null;
        let hasLevelTypeCompleted = false;

        // Check 1: Any Classification game session exists for this user?
        if (!hasAnySession) {
          determinedLevelType = sortedScenarioLevels[0]?.name;
          redirectUrl = '/classification/level/1/tutorial';

          if(!createGameSessionMutation.isPending) {
            safeCreateSession({ game: currentGameId, user: user?.id });
          }
        }
        // Check 2: All current game sessions completed?
        else if (currentGameSessions.every(session => session.completed)) {
          const canPlayAgain = currentGameSessions.length < 3 || isInstructor();

          redirectUrl = canPlayAgain
            ? '/classification/level/1/tutorial'
            : `/classification/level/${sortedScenarioLevels.length}`;

          if(canPlayAgain) {
            hasLevelTypeCompleted = hasCompletedLevelTypeBefore(
              sortedScenarioLevels[0]?.name,
              sessionsForGameType
            );

            if(!createGameSessionMutation.isPending) {
              safeCreateSession({ game: currentGameId, user: user?.id });
            }
          }
        }
        // Check 3: Has incomplete session - find where user left off
        else {
          const incompleteSession = currentGameSessions.find(session => !session.completed);
          let incompleteLevelFound = false;

          for (const scenarioLevel of sortedScenarioLevels) {
          	const isLevelCompleted = incompleteSession[`level${scenarioLevel.level}`] === 'completed';

            if (!isLevelCompleted) {
              determinedLevelType = scenarioLevel.name;

              hasLevelTypeCompleted = hasCompletedLevelTypeBefore(
                scenarioLevel.name,
                sessionsForGameType
              );

              const hasLevelTypeAnyDecisions = hasAnyDecisionsForLevelType(
                scenarioLevel.name,
                sessionsForGameType
              );

              const hasDefaultLevelTypeAnyDecisions = hasAnyDecisionsForLevelType(
                'class-default',
                sessionsForGameType
              );
              const hasTrickyLevelTypeAnyDecisions = hasAnyDecisionsForLevelType(
                'class-tricky',
                sessionsForGameType
              );

              // classified-multistep and realco share the same tutorial.
              // If the student has completed EITHER type before, skip
              // the tutorial for either upcoming level — they already
              // know the multistep gameplay.
              const isRealcoLevel = scenarioLevel.name?.startsWith(REALCO_PREFIX);
              const isMultistepLevel = scenarioLevel.name === 'classified-multistep';
              const sharesMultistepTutorial = isRealcoLevel || isMultistepLevel;

              const hasCompletedMultistepBefore = hasCompletedLevelTypeBefore(
                'classified-multistep',
                sessionsForGameType
              );
              // matchesLevelName prefix-matches when target starts with
              // REALCO_PREFIX, so passing the bare prefix matches any
              // `real-public-co-*` completed level.
              const hasCompletedRealcoBefore = hasCompletedLevelTypeBefore(
                REALCO_PREFIX,
                sessionsForGameType
              );
              const skipMultistepTutorial = sharesMultistepTutorial
                && (hasCompletedMultistepBefore || hasCompletedRealcoBefore);

              const shouldShowTutorial = !skipMultistepTutorial && (
                (!hasLevelTypeAnyDecisions && scenarioLevel.name !== 'class-default' && scenarioLevel.name !== 'class-tricky')
                || ((scenarioLevel.name === 'class-default' || scenarioLevel.name === 'class-tricky') && (!hasDefaultLevelTypeAnyDecisions && !hasTrickyLevelTypeAnyDecisions))
              );

              redirectUrl = shouldShowTutorial
                ? `/classification/level/${scenarioLevel.level}/tutorial`
                : `/classification/level/${scenarioLevel.level}`;

              incompleteLevelFound = true;
              break;
            }
          }

          if (!incompleteLevelFound) {
            redirectUrl = '/classification/feedback';
          }
        }

        setLevelType(determinedLevelType);
        setTransitionType(hasLevelTypeCompleted ? 'default' : 'initial');
      } if (isNormal) {
        let determinedLevelType = null;
        let hasLevelTypeCompleted = false;

        // Check 1: Any Normal game session exists for this user?
        if (!hasAnySession) {
          determinedLevelType = sortedScenarioLevels[0]?.name;
          redirectUrl = '/normal/level/1/tutorial';

          if(!createGameSessionMutation.isPending) {
            safeCreateSession({ game: currentGameId, user: user?.id });
          }
        }
        // Check 2: All current game sessions completed?
        else if (currentGameSessions.every(session => session.completed)) {
          const canPlayAgain = currentGameSessions.length < 3 || isInstructor();

          redirectUrl = canPlayAgain
            ? `/normal/level/1/tutorial`
            : `/normal/level/${sortedScenarioLevels.length}`;

          if(canPlayAgain) {
            hasLevelTypeCompleted = hasCompletedLevelTypeBefore(
              sortedScenarioLevels[0]?.name,
              sessionsForGameType
            );

            if(!createGameSessionMutation.isPending) {
              safeCreateSession({ game: currentGameId, user: user?.id });
            }
          }
        }
        // Check 3: Has incomplete session - find where user left off
        else {
          const incompleteSession = currentGameSessions.find(session => !session.completed);
          let incompleteLevelFound = false;

          for (const scenarioLevel of sortedScenarioLevels) {
          	const isLevelCompleted = incompleteSession[`level${scenarioLevel.level}`] === 'completed';

            if (!isLevelCompleted) {
              determinedLevelType = scenarioLevel.name;

              hasLevelTypeCompleted = hasCompletedLevelTypeBefore(
                scenarioLevel.name,
                sessionsForGameType
              );

              const hasLevelTypeAnyDecisions = hasAnyDecisionsForLevelType(
                scenarioLevel.name,
                sessionsForGameType
              );

              redirectUrl = !hasLevelTypeAnyDecisions && scenarioLevel.name === 'norm-elements'
                ? `/normal/level/${scenarioLevel.level}/tutorial`
                : `/normal/level/${scenarioLevel.level}`;

              incompleteLevelFound = true;
              break;
            }
          }

          if (!incompleteLevelFound) {
            redirectUrl = `/normal/level/${sortedScenarioLevels.length}`;
          }
        }

        setLevelType(determinedLevelType);
        setTransitionType(hasLevelTypeCompleted ? 'default' : 'initial');
      } else if (isEquation) {
        let determinedLevelType = null;
        let hasLevelTypeCompleted = false;

		    // Check 1: Any Balanced Equation game session exists for this user?
        if (!hasAnySession) {
          determinedLevelType = sortedScenarioLevels[0]?.name;
          redirectUrl = '/balanced-equation/level/1/tutorial';

          if(!createGameSessionMutation.isPending) {
            safeCreateSession({ game: currentGameId, user: user?.id });
          }
        }
        // Check 2: All current game sessions completed?
        else if (currentGameSessions.every(session => session.completed)) {
          const canPlayAgain = currentGameSessions.length < 3 || isInstructor();

          redirectUrl = canPlayAgain
            ? '/balanced-equation/level/1/intro'
            : `/balanced-equation/level/${sortedScenarioLevels.length}`;

          if (canPlayAgain) {
            hasLevelTypeCompleted = hasCompletedLevelTypeBefore(
              sortedScenarioLevels[0]?.name,
              sessionsForGameType
            );

            if(!createGameSessionMutation.isPending) {
              safeCreateSession({ game: currentGameId, user: user?.id });
            }
          }
        }
        // Check 3: Has incomplete session - find where user left off
        else {
          const incompleteSession = currentGameSessions.find(session => !session.completed);
          let incompleteLevelFound = false;

          for (const scenarioLevel of sortedScenarioLevels) {
            const isLevelCompleted = incompleteSession[`level${scenarioLevel.level}`] === 'completed';

            if (!isLevelCompleted) {
              determinedLevelType = scenarioLevel.name;

              hasLevelTypeCompleted = hasCompletedLevelTypeBefore(
                scenarioLevel.name,
                sessionsForGameType
              );

              const hasLevelTypeAnyDecisions = hasAnyDecisionsForLevelType(
                scenarioLevel.name,
                sessionsForGameType
              );

              const hasAccountsImpactedLevelTypeAnyDecisions = hasAnyDecisionsForLevelType(
                'accounts-impacted',
                sessionsForGameType
              );
              const hasIncomeStatementImpactLevelTypeAnyDecisions = hasAnyDecisionsForLevelType(
                'income-statement-impact',
                sessionsForGameType
              );

              const shouldShowIntro = scenarioLevel.design.includes('2');

              const basePath = `/balanced-equation/level/${scenarioLevel.level}`;
              if (
                (!hasLevelTypeAnyDecisions && scenarioLevel.name !== 'debit-credit' && scenarioLevel.name !== 'debit-credit2' && scenarioLevel.name !== 'all-types' && scenarioLevel.name !== 'accounts-impacted' && scenarioLevel.name !== 'income-statement-impact')
                || ((scenarioLevel.name === 'accounts-impacted' || scenarioLevel.name === 'income-statement-impact') && (!hasAccountsImpactedLevelTypeAnyDecisions && !hasIncomeStatementImpactLevelTypeAnyDecisions))) {
                redirectUrl = `${basePath}/tutorial`;
              } else if (hasLevelTypeCompleted && shouldShowIntro) {
                redirectUrl = `${basePath}/intro`;
              } else {
                redirectUrl = basePath;
              }

              incompleteLevelFound = true;
              break;
            }
          }

          if (!incompleteLevelFound) {
            redirectUrl = `/balanced-equation/level/${sortedScenarioLevels.length}`;
          }
        }

        setLevelType(determinedLevelType);
        setTransitionType(hasLevelTypeCompleted ? 'default' : 'initial');
      } else if(isPrinciples) {
        let shouldShowInitial = false;

        let determinedLevelType = null;

        // Only these level types have a hardcoded frontend tutorial.
        const tutorialLevelTypes = ['revenue-recognition', 'expense-recognition'];
        // Level types that have a per-level intro page before the level itself.
        const introLevelTypes = ['assumptions-principles-constraints'];

        const resolveLevelPath = (scenarioLevel, hasLevelTypeDecisions) => {
          const basePath = `/the-principles/level/${scenarioLevel.level}`;
          const needsTutorial =
            tutorialLevelTypes.includes(scenarioLevel.name) && !hasLevelTypeDecisions;
          if (needsTutorial) return `${basePath}/tutorial`;
          if (introLevelTypes.includes(scenarioLevel.name)) return `${basePath}/level-intro`;
          return basePath;
        };

        // Check 1: Any The Principles game session exists for this user?
        if (!hasAnySession) {
          shouldShowInitial = true;

          const firstLevel = sortedScenarioLevels[0];
          determinedLevelType = firstLevel?.name;

          redirectUrl = firstLevel
            ? resolveLevelPath(firstLevel, false)
            : `/the-principles/level/1`;

          if(!createGameSessionMutation.isPending) {
            safeCreateSession({ game: currentGameId, user: user?.id });
          }
        }
        // Check 2: All current game sessions completed?
        else if (currentGameSessions.every(session => session.completed)) {
          const canPlayAgain = currentGameSessions.length < 3 || isInstructor();

          // On replays, bypass the legacy /level/1/intro page entirely
          // and let resolveLevelPath pick the right entry for the first
          // level (tutorial / level-intro / level itself), mirroring
          // Check 1's no-sessions path.
          const firstLevel = sortedScenarioLevels[0];
          redirectUrl = canPlayAgain && firstLevel
            ? resolveLevelPath(firstLevel, false)
            : `/the-principles/level/${sortedScenarioLevels.length}`;

          if(canPlayAgain) {
            shouldShowInitial = !hasCompletedLevelTypeBefore(sortedScenarioLevels[0]?.name, sessionsForGameType);

            if(!createGameSessionMutation.isPending) {
              safeCreateSession({ game: currentGameId, user: user?.id });
            }
          }
        }
        // Check 3: Has incomplete session - find where user left off
        else {
          const incompleteSession = currentGameSessions.find(session => !session.completed);
          let incompleteLevelFound = false;

          for (const scenarioLevel of sortedScenarioLevels) {
            const isLevelCompleted = incompleteSession[`level${scenarioLevel.level}`] === 'completed';

            if (!isLevelCompleted) {
              determinedLevelType = scenarioLevel.name;

              shouldShowInitial = !hasCompletedLevelTypeBefore(scenarioLevel.name, sessionsForGameType);

              // Show the tutorial only on first entry to a supported level
              // (no decisions recorded yet for this level type).
              const hasLevelTypeAnyDecisions = hasAnyDecisionsForLevelType(
                scenarioLevel.name,
                sessionsForGameType
              );

              redirectUrl = resolveLevelPath(scenarioLevel, hasLevelTypeAnyDecisions);

              incompleteLevelFound = true;
              break;
            }
          }

          if (!incompleteLevelFound) {
            redirectUrl = '/the-principles/summary';
          }
        }

        setLevelType(determinedLevelType);
        setTransitionType(shouldShowInitial ? 'initial' : 'default');
      }

      setRedirectUrl(redirectUrl);
      setIsDataReady(true);
    }

    processTransition();
  }, [
    gameType,
    scenarioLevels,
    currentGameId,
    gamesSessions,
    user?.id,
    games,
    isClassification,
    isNormal,
    isPrinciples,
    isEquation,
    dataUpdatedAtGamesSessions
  ]);

  const handleInitialComplete = useCallback(() => {
    if (redirectUrl) {
      navigate(redirectUrl, { replace: true });
    }
  }, [redirectUrl, navigate]);

  return (
    <Layout isHeaderVisible={true}>
      {transitionType === 'default' && (
        <TransitionDefault
          gameType={gameType}
          isInstructor={isInstructor()}
          isStudent={isStudent()}
          user={user}
        />
      )}

      {transitionType === 'initial' && (
        <TransitionInitial
          gameType={gameType}
          levelType={levelType}
          onComplete={handleInitialComplete}
          numberOfAttempts={courses?.[0]?.number_of_attempts}
          businessType={courses?.[0]?.businesstype}
        />
      )}

      {sessionError && (
        <Alert
          type={sessionError.type}
          title={sessionError.title}
          text={sessionError.text}
          confirmButtonText="Back to Assignments"
          onConfirmButtonClick={() => {
            setSessionError(null);
            navigate('/assignments', { replace: true });
          }}
        />
      )}
    </Layout>
  );
}
